// react/libs
import dayjs from 'dayjs';
import { useContext, useEffect, useState } from 'react';

// constants
import { CALENDAR_INTERACTION_SMART_ACTION_DIALOG_ID } from './CalendarInteractionProvider.consts';

// utils
import { convertFormFieldDataToPayload } from 'components/shared/DialogProvider/dialogs/SmartActionDialog/SmartActionDialog.utils';
import {
  showErrorSnackbar,
  showSuccessSnackbar
} from 'components/shared/Snackbar';
import { mutations } from 'RestAPIs/rapini';
import { createAppointment } from './utils/createAppointment';
import { duplicateEvent as duplicateEventUtil } from './utils/duplicateEvent';
import { pasteEvent as pasteEventUtil } from './utils/pasteEvent';

// stores/hooks/context
import { appointmentFormFieldEntitiesToPlanningEntityIdentifiers } from 'components/mrp/components/AppointmentForm/AppointmentForm.utils';
import { useEntityListener } from 'components/mrp/mrp.hooks';
import { useAppointmentStore } from 'components/mrp/stores/appointmentStore/appointmentStore';
import { useCalendarStore } from 'components/mrp/stores/calendarStore/calendarStore';
import { DialogContext } from 'components/shared/DialogProvider/DialogContext';
import { DialogType } from 'components/shared/DialogProvider/DialogProvider.enums';
import { useShallow } from 'zustand/shallow';
import { getPlanningEntityIdentifiersFromEvent } from '../../../Calendar/components/CalendarAppointment/CalenderAppointment.utils';
import { useCalendarInteractionStore } from '../store/calendarInteractionStore.store';
import { CalendarInteractionContext } from './CalendarInteractionProvider.context';

// types
import type { AppointmentFormFieldData } from 'components/mrp/components/AppointmentForm/AppointmentForm.types';
import type { CalendarAppointmentEvent } from 'components/mrp/components/Calendar/Calendar.types';
import type {
  ExportCalculatedAppointmentOccurrencesBySeriesRequest,
  PlanningEntityIdentifier
} from 'generated/index';
import type { ReactNode } from 'react';

export const CalendarInteractionProvider = ({
  children
}: {
  children: ReactNode | ReactNode[];
}) => {
  'use memo';
  const { sourceEvent, isCut, isProcessing, setSourceEvent, setIsProcessing } =
    useCalendarInteractionStore(
      useShallow((state) => ({
        sourceEvent: state.sourceEvent,
        isCut: state.isCut,
        isProcessing: state.isProcessing,
        setSourceEvent: state.control.setSourceEvent,
        setIsProcessing: state.control.setIsProcessing
      }))
    );

  const { calendars, timeStep } = useCalendarStore(
    useShallow((state) => ({
      calendars: state.calendars,
      timeStep: state.timeStep
    }))
  );

  const { openDialog, onClose } = useContext(DialogContext);

  const [fetchEntities, setFetchEntities] = useState<
    PlanningEntityIdentifier[]
  >([]);

  const { refetchByPlanningEntityIdentifiers } = useEntityListener({
    listen: false,
    planningEntityIdentifiers: []
  });

  useEffect(() => {
    onClose?.(
      CALENDAR_INTERACTION_SMART_ACTION_DIALOG_ID,
      DialogType.SMART_ACTION_DIALOG,
      async (props) => {
        if (props?.ok) {
          try {
            await refetchByPlanningEntityIdentifiers(fetchEntities);
          } catch (error) {
            console.error('Failed to refetch planning entities', error);
          }
        }

        if (isCut) {
          setSourceEvent(undefined, false);
        }

        setIsProcessing(false);
      }
    );
  }, [
    fetchEntities,
    isCut,
    onClose,
    refetchByPlanningEntityIdentifiers,
    setIsProcessing,
    setSourceEvent
  ]);

  const { appointment, setAppointmentData } = useAppointmentStore(
    useShallow((state) => ({
      appointment: state.appointment,
      setAppointmentData: state.control.setAppointmentData
    }))
  );

  const { mutateAsync: createAppointmentMutation } =
    mutations.useCreateAppointment();

  const { mutateAsync: exportAppointmentSeriesMutation } =
    mutations.useExportCalculatedAppointmentOccurrencesBySeries();

  const createAppointmentDirectly = async (
    formData: AppointmentFormFieldData,
    entitiesToRefetch: PlanningEntityIdentifier[]
  ) => {
    // For duplicates, suppress warnings (overlaps are expected)
    const payload = convertFormFieldDataToPayload(formData, true);

    return createAppointmentMutation(payload, {
      onSuccess: async () => {
        showSuccessSnackbar();
        // Refresh the calendar with the entities that need updating
        await refetchByPlanningEntityIdentifiers(entitiesToRefetch);
      },
      onError: () => {
        showErrorSnackbar();
      }
    });
  };

  const exportAppointmentSeriesDirectly = async (
    event: CalendarAppointmentEvent,
    entitiesToRefetch: PlanningEntityIdentifier[]
  ) => {
    if (!event || isProcessing) return;

    const date = dayjs(event.planningEvent.start);
    const startDate = date.startOf('day');
    const endDate = startDate.add(1, 'year');

    const payload = {
      originalAppointmentId: event.planningEvent?.originalAppointmentId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    } as ExportCalculatedAppointmentOccurrencesBySeriesRequest;

    return exportAppointmentSeriesMutation(payload, {
      onSuccess: async () => {
        showSuccessSnackbar();
        // Refresh the calendar with the entities that need updating
        await refetchByPlanningEntityIdentifiers(entitiesToRefetch);
      },
      onError: () => {
        showErrorSnackbar();
      }
    });
  };

  const pasteEvent = (calendarId: string, date: dayjs.Dayjs) => {
    if (!sourceEvent || !calendarId || !date || isProcessing) return;

    pasteEventUtil({
      sourceEvent,
      calendars,
      isCut,
      calendarId,
      date,
      setIsProcessing,
      setFetchEntities,
      openDialog
    });
  };

  const addAppointment = (
    calendarId?: string,
    start?: dayjs.Dayjs,
    durationInMinutes?: number
  ) => {
    if (!!appointment) return;

    const calendar = calendars[calendarId || '-1'];
    const actualDuration = durationInMinutes || timeStep;

    const formData = createAppointment({
      start,
      calendar,
      durationInMinutes: actualDuration
    });

    setAppointmentData(formData);
  };

  const updateAppointment = (
    appointment?: AppointmentFormFieldData | null,
    oldEntities: PlanningEntityIdentifier[] = []
  ) => {
    if (!appointment) return;

    const entities = appointmentFormFieldEntitiesToPlanningEntityIdentifiers(
      Object.values(appointment.planEntities).flatMap((entity) => entity)
    ) satisfies PlanningEntityIdentifier[];

    setFetchEntities([...entities, ...oldEntities]);

    openDialog?.(
      CALENDAR_INTERACTION_SMART_ACTION_DIALOG_ID,
      DialogType.SMART_ACTION_DIALOG,
      {
        actionType: 'update',
        appointmentExceptions: appointment.appointmentExceptions,
        payload: appointment
      }
    );
  };

  const deleteAppointment = (appointment?: AppointmentFormFieldData | null) => {
    if (!appointment) return;

    const entities = appointmentFormFieldEntitiesToPlanningEntityIdentifiers(
      Object.values(appointment.planEntities).flatMap((entity) => entity)
    ) satisfies PlanningEntityIdentifier[];

    setFetchEntities(entities);

    openDialog?.(
      CALENDAR_INTERACTION_SMART_ACTION_DIALOG_ID,
      DialogType.SMART_ACTION_DIALOG,
      {
        actionType: 'delete',
        appointmentExceptions: appointment.appointmentExceptions,
        payload: appointment
      }
    );
  };

  const exportAppointmentSeries = async (event: CalendarAppointmentEvent) => {
    if (!event || isProcessing) return;

    // Entities from the appointment
    const sourceEntities = getPlanningEntityIdentifiersFromEvent(
      event.planningEvent
    );
    await exportAppointmentSeriesDirectly(event, sourceEntities);
  };

  const duplicateEvent = async (event: CalendarAppointmentEvent) => {
    if (!event || isProcessing) return;

    await duplicateEventUtil({
      event,
      setIsProcessing,
      createAppointment: createAppointmentDirectly,
      asRecurring: false
    });
  };

  const duplicateRecurringEvent = async (event: CalendarAppointmentEvent) => {
    if (!event || isProcessing) return;

    await duplicateEventUtil({
      event,
      setIsProcessing,
      createAppointment: createAppointmentDirectly,
      asRecurring: true
    });
  };

  const contextValue = {
    pasteEvent,
    addAppointment,
    deleteAppointment,
    updateAppointment,
    duplicateEvent,
    duplicateRecurringEvent,
    exportAppointmentSeries
  };

  return (
    <CalendarInteractionContext.Provider value={contextValue}>
      {children}
    </CalendarInteractionContext.Provider>
  );
};
